// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { useCreateOctoRuleMutation } from "@/api/hooks/use-octo-rules";
import { SushiPlusIcon } from "@/components/icons";
import { CodeDialectBadge } from "@/components/shared/code-dialect-badge";
import { DatePicker } from "@/components/shared/date-picker";
import { FormLabelRow } from "@/components/shared/form-label-row";
import { VirtualSelectPopover } from "@/components/shared/virtual-select-popover";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { buildOctoRuleSchema, OCTO_RULE_VARIABLES } from "@/features/octo/octo-cel-schema";
import { PredicateFields } from "@/features/octo/predicate-fields";
import { PredicateWarning } from "@/features/octo/predicate-warning";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { useDebugMode } from "@/lib/debug-mode";
import { fieldsAtCursor, validateExpression } from "@/lib/expression-validator";
import { cn } from "@/lib/utils";

const STAGES = ["creation", "visibility", "workability"] as const;
const OUTCOMES = ["allow", "deny"] as const;
const SCOPES = ["global", "chain", "store"] as const;

const schema = z
  .object({
    // A rule targets exactly one of: everything (global), a chain, or a single store.
    scope: z.enum(SCOPES),
    chainName: z.string(),
    storeName: z.string(),
    // A rule must be named, and the name must be a single token — no spaces (all other characters,
    // including special chars, dashes and underscores, are allowed). See CIN-659.
    name: z.string().min(1, "Required").regex(/^\S+$/, "No spaces allowed"),
    description: z.string(),
    tickets: z.string(),
    stage: z.enum(STAGES),
    outcome: z.enum(OUTCOMES),
    priority: z.number().int(),
    predicate: z.string(),
    startDate: z.string(),
    endDate: z.string(),
  })
  .superRefine((v, ctx) => {
    if (v.scope === "chain" && !v.chainName)
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Required", path: ["chainName"] });
    if (v.scope === "store" && !v.storeName)
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Required", path: ["storeName"] });
  });

type CreateOctoRuleValues = z.infer<typeof schema>;

const DEFAULTS: CreateOctoRuleValues = {
  scope: "global",
  chainName: "",
  storeName: "",
  name: "",
  description: "",
  tickets: "",
  stage: "creation",
  outcome: "allow",
  priority: 0,
  predicate: "",
  startDate: "",
  endDate: "",
};

interface CreateOctoRuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateOctoRuleDialog({ open, onOpenChange }: CreateOctoRuleDialogProps) {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const { debugMode } = useDebugMode();
  const { data: context } = useBackofficeContext();
  const createRule = useCreateOctoRuleMutation();

  const form = useForm<CreateOctoRuleValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULTS,
  });

  useEffect(
    function resetFormOnOpen() {
      if (open) form.reset(DEFAULTS);
    },
    [open, form],
  );

  // Live CEL validation — syntax + known-variable check blocks save; unknown properties only warn.
  // task.type is enum-checked against the live task types from context (canonical + custom).
  const predicateRaw = form.watch("predicate");
  const predicate = predicateRaw.trim();
  const octoSchema = useMemo(() => buildOctoRuleSchema(context?.taskTypes), [context?.taskTypes]);
  const predicateResult = useMemo(() => validateExpression(predicate, "cel", octoSchema), [predicate, octoSchema]);
  const predicateInvalid = predicate.length > 0 && !predicateResult.valid;
  // Read-only "available fields" hint driven by the caret position within the predicate editor.
  const [predicateCaret, setPredicateCaret] = useState<number | null>(null);
  const fieldHint = useMemo(
    () => (predicateCaret == null ? null : fieldsAtCursor(octoSchema, predicateRaw.slice(0, predicateCaret))),
    [octoSchema, predicateRaw, predicateCaret],
  );
  const predicateWarning = predicate.length > 0 && predicateResult.valid ? predicateResult.warning : undefined;

  async function onSubmit(values: CreateOctoRuleValues) {
    try {
      const res = await createRule.mutateAsync({
        chainName: values.scope === "chain" ? values.chainName : undefined,
        storeName: values.scope === "store" ? values.storeName : undefined,
        name: values.name.trim() || undefined,
        description: values.description.trim() || undefined,
        tickets: values.tickets.trim() || undefined,
        stage: values.stage,
        outcome: values.outcome,
        priority: values.priority,
        predicate: values.predicate.trim() || undefined,
        startDate: values.startDate || undefined,
        endDate: values.endDate || undefined,
      });
      toast.success(t("octoRules.dialog.created", { name: values.name || `#${res.octoRuleId}` }));
      onOpenChange(false);
      navigate({ to: "/octo/rules/$ruleId/edit", params: { ruleId: String(res.octoRuleId) }, search: {} });
    } catch {
      // Error toast handled by API middleware
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        id="dialog-create-octo-rule"
        className="sm:max-w-2xl p-0 gap-0 overflow-hidden flex flex-col"
        aria-describedby={undefined}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col">
            <div className="flex flex-row">
              <WizardPane icon={SushiPlusIcon} />
              <div className="flex-1 p-6 flex flex-col gap-4 min-w-0 max-h-[72vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{t("octoRules.dialog.createTitle")}</DialogTitle>
                </DialogHeader>

                <div className="flex flex-wrap items-start gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem className="flex-1 min-w-48">
                        <FormLabelRow label={t("octoRules.fields.name")} />
                        <FormControl>
                          <Input {...field} autoComplete="off" />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="tickets"
                    render={({ field }) => (
                      <FormItem className="w-1/4 min-w-36">
                        <FormLabelRow label={t("octoRules.fields.tickets")} />
                        <FormControl>
                          <Input {...field} className="font-mono" autoComplete="off" />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabelRow label={t("octoRules.fields.description")} />
                      <FormControl>
                        <Textarea {...field} rows={2} className="min-h-14" />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <Separator />

                {/* Scope — a rule targets exactly one of: everything (global), a chain, or a
                    single store. The matching selector appears once a chain/store scope is chosen. */}
                <div className="flex flex-wrap items-start gap-4">
                  <FormField
                    control={form.control}
                    name="scope"
                    render={({ field }) => (
                      <FormItem className="w-40">
                        <FormLabel>{t("octoRules.form.scopeLabel")}</FormLabel>
                        <Select
                          value={field.value}
                          onValueChange={(v) => {
                            field.onChange(v);
                            // Clear the other scope's selection so only one is ever sent.
                            form.setValue("chainName", "");
                            form.setValue("storeName", "");
                          }}
                        >
                          <FormControl>
                            <SelectTrigger className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="global">{t("octoRules.scopeGlobal")}</SelectItem>
                            <SelectItem value="chain">{t("octoRules.form.chain")}</SelectItem>
                            <SelectItem value="store">{t("octoRules.form.store")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                  {form.watch("scope") === "chain" && (
                    <FormField
                      control={form.control}
                      name="chainName"
                      render={({ field }) => (
                        <FormItem className="flex-1 min-w-40">
                          <FormLabelRow label={t("octoRules.form.chain")} />
                          <FormControl>
                            <VirtualSelectPopover
                              value={field.value}
                              onChange={field.onChange}
                              options={(context?.chains ?? []).map((c) => ({ label: c.display, value: c.value }))}
                              placeholder={t("octoRules.form.selectChain")}
                              searchPlaceholder={t("stores.form.searchChains")}
                              emptyText={t("stores.form.noChains")}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  )}
                  {form.watch("scope") === "store" && (
                    <FormField
                      control={form.control}
                      name="storeName"
                      render={({ field }) => (
                        <FormItem className="flex-1 min-w-40">
                          <FormLabelRow label={t("octoRules.form.store")} />
                          <FormControl>
                            <VirtualSelectPopover
                              value={field.value}
                              onChange={field.onChange}
                              options={(context?.stores ?? []).map((s) => ({ label: s.display, value: s.value }))}
                              placeholder={t("octoRules.form.selectStore")}
                              searchPlaceholder={t("users.list.filter.searchStores")}
                              emptyText={t("users.list.filter.noStores")}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  )}
                </div>

                <Separator />

                <div className="flex flex-wrap gap-4">
                  <FormField
                    control={form.control}
                    name="stage"
                    render={({ field }) => (
                      <FormItem className="flex-1 min-w-40">
                        <FormLabelRow label={t("octoRules.fields.stage")} />
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {STAGES.map((s) => (
                              <SelectItem key={s} value={s}>
                                {t(`octoRules.stage.${s}`)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="outcome"
                    render={({ field }) => (
                      <FormItem className="flex-1 min-w-32">
                        <FormLabelRow label={t("octoRules.fields.outcome")} />
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {OUTCOMES.map((o) => (
                              <SelectItem key={o} value={o}>
                                {t(`octoRules.outcome.${o}`)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="priority"
                    render={({ field }) => (
                      <FormItem className="w-28">
                        <FormLabelRow label={t("octoRules.fields.priority")} />
                        <FormControl>
                          <Input
                            type="number"
                            className="font-mono tabular-nums"
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.valueAsNumber || 0)}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>

                <Separator />

                <FormField
                  control={form.control}
                  name="predicate"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between">
                        <FormLabel>{t("octoRules.fields.predicate")}</FormLabel>
                        <CodeDialectBadge dialect="cel" />
                      </div>
                      <FormControl>
                        <Textarea
                          {...field}
                          rows={3}
                          spellCheck={false}
                          onSelect={(e) => setPredicateCaret(e.currentTarget.selectionStart)}
                          onKeyUp={(e) => setPredicateCaret(e.currentTarget.selectionStart)}
                          onClick={(e) => setPredicateCaret(e.currentTarget.selectionStart)}
                          onBlur={() => {
                            field.onBlur();
                            setPredicateCaret(null);
                          }}
                          className={cn(
                            "min-h-14 break-words font-mono",
                            predicateInvalid && "border-destructive focus-visible:ring-destructive/40",
                            predicateWarning && "border-amber-500 focus-visible:ring-amber-500/40",
                          )}
                        />
                      </FormControl>
                      {predicateInvalid && (
                        <span className="text-sm text-destructive">
                          {t("octoRules.edit.predicateInvalid")}
                          {predicateResult.error ? ` — ${predicateResult.error}` : ""}
                        </span>
                      )}
                      {predicateWarning && (
                        <PredicateWarning
                          message={predicateWarning}
                          kind={predicateResult.warningKind}
                          options={predicateResult.warningOptions}
                        />
                      )}
                    </FormItem>
                  )}
                />
                <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
                  <span className="shrink-0 py-0.5">{t("octoRules.edit.variables")}:</span>
                  <div className="flex flex-wrap gap-1.5">
                    {OCTO_RULE_VARIABLES.map((variable) => (
                      <code key={variable} className="rounded bg-muted px-1.5 py-0.5 font-mono">
                        {variable}
                      </code>
                    ))}
                  </div>
                </div>
                {fieldHint && <PredicateFields path={fieldHint.path} fields={fieldHint.fields} />}

                {/* Start/end date are not wired up in the backend yet — debug-mode only for now. */}
                {debugMode && (
                  <>
                    <Separator />
                    <div className="flex flex-wrap gap-4">
                      <FormField
                        control={form.control}
                        name="startDate"
                        render={({ field }) => (
                          <FormItem className="flex-1 min-w-40">
                            <FormLabelRow label={t("octoRules.fields.startDate")} />
                            <FormControl>
                              <DatePicker value={field.value || undefined} onChange={(v) => field.onChange(v ?? "")} />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="endDate"
                        render={({ field }) => (
                          <FormItem className="flex-1 min-w-40">
                            <FormLabelRow label={t("octoRules.fields.endDate")} />
                            <FormControl>
                              <DatePicker value={field.value || undefined} onChange={(v) => field.onChange(v ?? "")} />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  form.reset(DEFAULTS);
                  onOpenChange(false);
                }}
              >
                {t("buttons.cancel")}
              </Button>
              <Button type="submit" disabled={createRule.isPending || predicateInvalid}>
                {createRule.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <SushiPlusIcon className="h-4 w-4" />
                )}
                {t("buttons.create")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
